import { useParams, useNavigate } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from '@/components/ui/alert-dialog';
import { ArrowLeft, Trash2, Play, Eye } from 'lucide-react';
import { toast } from 'sonner';
import { useState } from 'react';
import { logger } from '@/services/logger';
import {
  useAdminGetBroadcast,
  useAdminGetNotificationTemplate,
  useAdminUpdateBroadcast,
  useAdminDeleteBroadcast,
  useAdminDryRunBroadcast,
  useAdminTriggerBroadcast,
  getAdminGetBroadcastQueryKey,
} from '@/api/auth/admin/admin';
import {
  BroadcastDryRunResponse,
  BroadcastResponseStatus,
  UpdateBroadcastRequestRecipientType,
} from '@/api/auth/missionImpactAuthAPI.schemas';
import { BroadcastFiltersEditor } from '@/components/admin/broadcasts/BroadcastFiltersEditor';
import { BroadcastNotificationsCard } from '@/components/admin/broadcasts/BroadcastNotificationsCard';
import { DryRunResultsDialog } from '@/components/admin/broadcasts/DryRunResultsDialog';
import { parseFiltersFromApi } from '@/types/broadcast-filters';

/**
 * Admin Broadcast detail page for managing a broadcast notification
 */
export default function AdminBroadcastDetailPage() {
  const { broadcastId } = useParams<{ broadcastId: string }>();
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const [editedName, setEditedName] = useState<string | null>(null);
  const [dryRunResults, setDryRunResults] = useState<BroadcastDryRunResponse | null>(null);
  const [showDryRunDialog, setShowDryRunDialog] = useState(false);

  const {
    data: broadcastData,
    isLoading,
    error,
  } = useAdminGetBroadcast(broadcastId!, {
    query: { enabled: !!broadcastId },
  });

  const broadcast = broadcastData?.data;

  const { data: templateData } = useAdminGetNotificationTemplate(broadcast?.templateId!, {
    query: { enabled: !!broadcast?.templateId },
  });

  const templateIsActive = templateData?.data?.isActive ?? true;
  const canSendBroadcast = templateIsActive;

  const { mutate: updateBroadcast, isPending: isUpdating } = useAdminUpdateBroadcast({
    mutation: {
      onSuccess: () => {
        toast.success('Broadcast updated');
        queryClient.invalidateQueries({ queryKey: getAdminGetBroadcastQueryKey(broadcastId!) });
        setEditedName(null);
      },
      onError: (error: any) => {
        logger.error(error, { context: 'UpdateBroadcast', broadcastId });
        toast.error(
          'Failed to update broadcast: ' + (error.response?.data?.detail || error.message)
        );
      },
    },
  });

  const { mutate: deleteBroadcast, isPending: isDeleting } = useAdminDeleteBroadcast({
    mutation: {
      onSuccess: () => {
        toast.success('Broadcast deleted');
        navigate(-1);
      },
      onError: (error: any) => {
        logger.error(error, { context: 'DeleteBroadcast', broadcastId });
        toast.error(
          'Failed to delete broadcast: ' + (error.response?.data?.detail || error.message)
        );
      },
    },
  });

  const { mutate: dryRunBroadcast, isPending: isDryRunning } = useAdminDryRunBroadcast({
    mutation: {
      onSuccess: (response) => {
        setDryRunResults(response.data);
        setShowDryRunDialog(true);
        toast.success(`Dry run complete: ${response.data.totalRecipients} recipients`);
      },
      onError: (error: any) => {
        logger.error(error, { context: 'DryRunBroadcast', broadcastId });
        toast.error('Dry run failed: ' + (error.response?.data?.detail || error.message));
      },
    },
  });

  const { mutate: triggerBroadcast, isPending: isTriggering } = useAdminTriggerBroadcast({
    mutation: {
      onSuccess: () => {
        toast.success('Broadcast triggered');
        queryClient.invalidateQueries({ queryKey: getAdminGetBroadcastQueryKey(broadcastId!) });
      },
      onError: (error: any) => {
        logger.error(error, { context: 'TriggerBroadcast', broadcastId });
        toast.error(
          'Failed to trigger broadcast: ' + (error.response?.data?.detail || error.message)
        );
      },
    },
  });

  const isDraft = broadcast?.status === BroadcastResponseStatus.Draft;
  const filters = parseFiltersFromApi(broadcast?.filters);

  const getStatusBadge = (status?: BroadcastResponseStatus) => {
    switch (status) {
      case BroadcastResponseStatus.Draft:
        return <Badge variant="outline">Draft</Badge>;
      case BroadcastResponseStatus.Queued:
        return <Badge variant="secondary">Queued</Badge>;
      case BroadcastResponseStatus.InProgress:
        return <Badge className="bg-blue-500">In Progress</Badge>;
      case BroadcastResponseStatus.Completed:
        return <Badge className="bg-green-500">Completed</Badge>;
      case BroadcastResponseStatus.Failed:
        return <Badge variant="destructive">Failed</Badge>;
      default:
        return <Badge variant="outline">Unknown</Badge>;
    }
  };

  const handleSaveName = () => {
    if (editedName && editedName !== broadcast?.name) {
      updateBroadcast({
        broadcastId: broadcastId!,
        data: { name: editedName },
      });
    } else {
      setEditedName(null);
    }
  };

  const handleDelete = () => {
    deleteBroadcast({ broadcastId: broadcastId! });
  };

  const handleDryRun = () => {
    dryRunBroadcast({ broadcastId: broadcastId! });
  };

  const handleTrigger = () => {
    triggerBroadcast({ broadcastId: broadcastId! });
  };

  const formatDate = (dateString?: string | null) => {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleString();
  };

  if (isLoading) {
    return (
      <div className="space-y-6">
        <Skeleton className="h-10 w-[200px]" />
        <Skeleton className="h-[200px] w-full" />
      </div>
    );
  }

  if (error || !broadcast) {
    return (
      <div className="space-y-6">
        <Button variant="ghost" onClick={() => navigate(-1)}>
          <ArrowLeft className="h-4 w-4 mr-2" />
          Back
        </Button>
        <div className="text-center text-destructive py-8">
          <p>Failed to load broadcast: {(error as any)?.message || 'Broadcast not found'}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center gap-4">
        <Button variant="ghost" onClick={() => navigate(-1)}>
          <ArrowLeft className="h-4 w-4 mr-2" />
          Back
        </Button>
      </div>

      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <h1 className="text-3xl font-bold">{broadcast.name}</h1>
          {getStatusBadge(broadcast.status)}
        </div>
        <div className="flex items-center gap-2">
          {isDraft && (
            <>
              <Button variant="outline" onClick={handleDryRun} disabled={isDryRunning}>
                <Eye className="h-4 w-4 mr-2" />
                {isDryRunning ? 'Running...' : 'Dry Run'}
              </Button>
              <AlertDialog>
                <AlertDialogTrigger asChild>
                  <Button
                    disabled={isTriggering || !canSendBroadcast}
                    title={!canSendBroadcast ? 'Template is disabled' : undefined}
                  >
                    <Play className="h-4 w-4 mr-2" />
                    {isTriggering ? 'Triggering...' : 'Send Broadcast'}
                  </Button>
                </AlertDialogTrigger>
                <AlertDialogContent>
                  <AlertDialogHeader>
                    <AlertDialogTitle>Send Broadcast?</AlertDialogTitle>
                    <AlertDialogDescription>
                      This will send the notification to all matching recipients. This action cannot
                      be undone.
                      {dryRunResults && (
                        <span className="block mt-2 font-medium">
                          Estimated recipients: {dryRunResults.totalRecipients}
                        </span>
                      )}
                    </AlertDialogDescription>
                  </AlertDialogHeader>
                  <AlertDialogFooter>
                    <AlertDialogCancel>Cancel</AlertDialogCancel>
                    <AlertDialogAction onClick={handleTrigger}>Send</AlertDialogAction>
                  </AlertDialogFooter>
                </AlertDialogContent>
              </AlertDialog>
            </>
          )}
          {isDraft && (
            <AlertDialog>
              <AlertDialogTrigger asChild>
                <Button variant="destructive" disabled={isDeleting}>
                  <Trash2 className="h-4 w-4 mr-2" />
                  Delete
                </Button>
              </AlertDialogTrigger>
              <AlertDialogContent>
                <AlertDialogHeader>
                  <AlertDialogTitle>Delete Broadcast?</AlertDialogTitle>
                  <AlertDialogDescription>
                    This action cannot be undone. The broadcast will be permanently deleted.
                  </AlertDialogDescription>
                </AlertDialogHeader>
                <AlertDialogFooter>
                  <AlertDialogCancel>Cancel</AlertDialogCancel>
                  <AlertDialogAction
                    onClick={handleDelete}
                    className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
                  >
                    Delete
                  </AlertDialogAction>
                </AlertDialogFooter>
              </AlertDialogContent>
            </AlertDialog>
          )}
        </div>
      </div>

      <Card>
        <CardHeader>
          <CardTitle>Details</CardTitle>
          <CardDescription>Broadcast configuration</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="name">Name</Label>
            {isDraft ? (
              <div className="flex gap-2">
                <Input
                  id="name"
                  value={editedName ?? broadcast.name ?? ''}
                  onChange={(e) => setEditedName(e.target.value)}
                  disabled={isUpdating}
                />
                {editedName !== null && editedName !== broadcast.name && (
                  <Button onClick={handleSaveName} disabled={isUpdating}>
                    {isUpdating ? 'Saving...' : 'Save'}
                  </Button>
                )}
              </div>
            ) : (
              <p className="text-sm">{broadcast.name}</p>
            )}
          </div>

          <div className="space-y-2">
            <Label>Template</Label>
            <p className="text-sm font-mono">{broadcast.templateEventName}</p>
          </div>

          <div className="space-y-2">
            <Label>Recipient Type</Label>
            {isDraft ? (
              <Select
                value={broadcast.recipientType}
                onValueChange={(value) => {
                  updateBroadcast({
                    broadcastId: broadcastId!,
                    data: { recipientType: value as UpdateBroadcastRequestRecipientType },
                  });
                }}
                disabled={isUpdating}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value={UpdateBroadcastRequestRecipientType.Users}>Users</SelectItem>
                  <SelectItem value={UpdateBroadcastRequestRecipientType.Orgs}>
                    Organizations
                  </SelectItem>
                </SelectContent>
              </Select>
            ) : (
              <p className="text-sm">{broadcast.recipientType}</p>
            )}
          </div>

          <div className="space-y-2">
            <Label>Created</Label>
            <p className="text-sm text-muted-foreground">{formatDate(broadcast.createdUtc)}</p>
          </div>
        </CardContent>
      </Card>

      <BroadcastFiltersEditor broadcastId={broadcastId!} filters={filters} disabled={!isDraft} />

      {!isDraft && <BroadcastNotificationsCard broadcastId={broadcastId!} />}

      <DryRunResultsDialog
        open={showDryRunDialog}
        onOpenChange={setShowDryRunDialog}
        results={dryRunResults}
      />
    </div>
  );
}
